import React, { useState, useRef, useMemo, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useI18n } from "@/i18n/I18nContext";
import api, { formatApiError } from "@/lib/api";
import { isoLocalDate } from "@/lib/constants";
import { reasonText, reasonsFromError } from "@/lib/sched";
import { Button } from "@/components/ui/button";
import { EmptyState } from "./ui";
import AppointmentModal, { linesFromAppointment } from "@/components/calendar/AppointmentModal";
import { WarningsDialog, ChoiceDialog } from "@/components/calendar/Dialogs";
import { useWaitlistEvents, useGaps, GapBlock, HoldBlock } from "@/components/waitlist/CalendarWaitlist";
import { WaitlistForm } from "@/components/waitlist/WaitlistForm";
import { useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus, Loader2, CalendarDays, Check, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const START_HOUR = 7, END_HOUR = 22, PX = 1; // 1px per minute
const DAY_START_MIN = START_HOUR * 60;
const GRID_HEIGHT = (END_HOUR - START_HOUR) * 60 * PX;
const SNAP = 5;

const minsFromDate = (iso) => { const d = new Date(iso); return d.getHours() * 60 + d.getMinutes(); };
const fmt = (iso) => { const d = new Date(iso); return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`; };
const fmtMin = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeek = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; };

export default function CalendarPage() {
    const { t, lang } = useI18n();
    const qc = useQueryClient();
    const [view, setView] = useState("day");
    const [cursor, setCursor] = useState(new Date());
    const [modal, setModal] = useState(null);
    const [dialog, setDialog] = useState(null); // {type:'warnings'|'choice', ...}
    const drag = useRef(null);
    const previewTimer = useRef(null);
    const [preview, setPreview] = useState(null);
    const [wlForm, setWlForm] = useState(false);
    const location = useLocation();
    useWaitlistEvents(t);

    const rangeStart = view === "week" ? startOfWeek(cursor) : new Date(cursor.getFullYear(), cursor.getMonth(), view === "month" ? 1 : cursor.getDate());
    const rangeEnd = view === "week" ? addDays(startOfWeek(cursor), 7) : view === "month" ? new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1) : addDays(rangeStart, 1);

    const { data: employees } = useQuery({ queryKey: ["employees"], queryFn: async () => (await api.get("/employees")).data });
    const { data: services } = useQuery({ queryKey: ["services"], queryFn: async () => (await api.get("/services")).data });
    const { data: customers } = useQuery({ queryKey: ["customers"], queryFn: async () => (await api.get("/customers")).data });
    const activeEmps = useMemo(() => (employees || []).filter((e) => e.active), [employees]);

    const qkey = ["appointments", view, isoLocalDate(rangeStart)];
    const { data: appts, isLoading } = useQuery({
        queryKey: qkey,
        queryFn: async () => {
            const s = new Date(rangeStart); s.setHours(0, 0, 0, 0);
            return (await api.get(`/appointments?start=${s.toISOString()}&end=${new Date(rangeEnd).toISOString()}`)).data;
        },
        refetchInterval: 30000, refetchOnWindowFocus: true,
    });
    const { data: gaps } = useGaps(isoLocalDate(cursor), view === "day");
    const refreshGaps = () => qc.invalidateQueries({ queryKey: ["waitlist-gaps"] });

    // "Handmatig boeken" from the waitlist: open the modal prefilled
    React.useEffect(() => {
        const e = location.state?.waitlistEntry;
        if (!e || !services || !employees) return;
        const empId = e.employee_id || activeEmps[0]?.id;
        const start = new Date(); start.setHours(9, 0, 0, 0); start.setDate(start.getDate() + 1);
        setModal({ mode: "create", data: { employee_id: empId, customer_id: e.customer_id, start, end: start, notes: e.notes || "", status: "scheduled",
            services: (e.service_ids || []).map((sid) => { const sv = services.find((x) => x.id === sid); return { service_id: sid, employee_id: empId, price: sv?.price, duration_min: sv?.duration_min, snapshot: {}, phases: [] }; }) } });
        window.history.replaceState({}, "");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state, services, employees]);

    const invalidate = () => { qc.invalidateQueries({ queryKey: ["appointments"] }); qc.invalidateQueries({ queryKey: ["waitlist-gaps"] }); };
    const replaceLocal = (updated) => qc.setQueryData(qkey, (old) => (old || []).map((a) => (a.id === updated.id ? updated : a)));

    const navPrev = () => setCursor((c) => view === "week" ? addDays(c, -7) : view === "month" ? new Date(c.getFullYear(), c.getMonth() - 1, 1) : addDays(c, -1));
    const navNext = () => setCursor((c) => view === "week" ? addDays(c, 7) : view === "month" ? new Date(c.getFullYear(), c.getMonth() + 1, 1) : addDays(c, 1));

    // ---- payload builder for a drag state ----
    const buildPatch = (d, p, opts = {}) => {
        const base = new Date(d.day); base.setHours(0, 0, 0, 0);
        const start = new Date(base.getTime() + p.start * 60000).toISOString();
        let lines = linesFromAppointment(d.appt);
        if (d.mode === "resize") {
            const idx = opts.serviceIdx ?? lines.length - 1;
            const newTotal = p.end - p.start;
            const others = lines.reduce((acc, l, i) => acc + (i === idx ? 0 : l.duration_min), 0);
            lines = lines.map((l, i) => (i === idx ? { ...l, duration_min: Math.max(5, newTotal - others) } : l));
        }
        if (d.mode === "move" && p.empId && p.empId !== d.origEmpId) {
            if (opts.onlyServiceIdx != null) lines = lines.map((l, i) => (i === opts.onlyServiceIdx ? { ...l, employee_id: p.empId } : l));
            else lines = lines.map((l) => ({ ...l, employee_id: p.empId }));
        }
        return { start, services: lines };
    };

    const runPreview = (d, p) => {
        clearTimeout(previewTimer.current);
        previewTimer.current = setTimeout(async () => {
            try {
                const body = buildPatch(d, p);
                const { data } = await api.post("/appointments/preview", { start: body.start, services: body.services, exclude_id: d.id });
                setPreview((cur) => (cur && cur.id === d.id && cur.start === p.start && cur.end === p.end && cur.empId === p.empId ? { ...cur, result: data } : cur));
            } catch { /* ignore */ }
        }, 120);
    };

    const onPointerMove = useCallback((e) => {
        const d = drag.current; if (!d) return;
        const dyMin = Math.round((e.clientY - d.startY) / PX / SNAP) * SNAP;
        let ns = d.origStart, ne = d.origEnd;
        if (d.mode === "move") { ns = d.origStart + dyMin; ne = d.origEnd + dyMin; } else { ne = Math.max(d.origStart + 5, d.origEnd + dyMin); }
        ns = Math.max(DAY_START_MIN, ns); ne = Math.min(END_HOUR * 60, ne);
        let empId = d.origEmpId;
        if (d.mode === "move") { const col = d.cols.find((c) => e.clientX >= c.left && e.clientX <= c.right); if (col) empId = col.id; }
        const p = { id: d.id, start: ns, end: ne, empId, result: null };
        setPreview((cur) => (cur && cur.start === ns && cur.end === ne && cur.empId === empId ? cur : p));
        runPreview(d, p);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const commit = async (d, body, force = false, undoBody = null) => {
        try {
            const { data } = await api.patch(`/appointments/${d.id}`, { ...body, force });
            replaceLocal(data);
            const undo = undoBody || { start: d.appt.start, services: linesFromAppointment(d.appt) };
            toast.success(`${t("sched.movedTo")} ${fmt(data.start)}`, {
                action: { label: t("sched.undo"), onClick: async () => { try { const r = await api.patch(`/appointments/${d.id}`, { ...undo, force: true }); replaceLocal(r.data); toast.success(t("sched.restored")); } catch (e) { toast.error(formatApiError(e)); invalidate(); } } },
            });
        } catch (e) {
            const { conflicts, warnings } = reasonsFromError(e);
            if (e?.response?.status === 422 && warnings.length) {
                setDialog({ type: "warnings", warnings, onConfirm: () => { setDialog(null); commit(d, body, true); } });
            } else if (conflicts.length) {
                toast.error(conflicts.map((c) => reasonText(t, c)).join(" "));
                invalidate();
            } else { toast.error(formatApiError(e)); invalidate(); }
        }
    };

    const onPointerUp = useCallback(async () => {
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
        clearTimeout(previewTimer.current);
        const d = drag.current; drag.current = null;
        setPreview((p) => {
            if (!d || !p || (p.start === d.origStart && p.end === d.origEnd && p.empId === d.origEmpId)) return null;
            if (p.result && p.result.conflicts?.length) {
                toast.error(p.result.conflicts.map((c) => reasonText(t, c)).join(" "));
                return null;
            }
            const lines = linesFromAppointment(d.appt);
            const multiEmp = new Set(lines.map((l) => l.employee_id)).size > 1;
            if (d.mode === "move" && p.empId !== d.origEmpId && multiEmp) {
                setDialog({ type: "choice", title: t("sched.whichParts"), options: [
                    { label: t("sched.entireAppointment"), onSelect: () => { setDialog(null); commit(d, buildPatch(d, p)); } },
                    ...lines.map((l, i) => ({ label: `${t("sched.onlyService")}: ${d.appt.services?.[i]?.name || ""}`, onSelect: () => { setDialog(null); commit(d, buildPatch(d, p, { onlyServiceIdx: i })); } })),
                ] });
                return null;
            }
            if (d.mode === "resize" && lines.length > 1) {
                setDialog({ type: "choice", title: t("sched.whichDuration"), options: lines.map((l, i) => ({ label: d.appt.services?.[i]?.name || `#${i + 1}`, onSelect: () => { setDialog(null); commit(d, buildPatch(d, p, { serviceIdx: i })); } })) });
                return null;
            }
            commit(d, buildPatch(d, p));
            return null;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onPointerMove, t]);

    const beginDrag = (e, appt, mode, day) => {
        e.stopPropagation();
        const cols = activeEmps.map((emp) => { const el = document.querySelector(`[data-testid="day-col-${emp.id}"]`); const r = el?.getBoundingClientRect(); return r ? { id: emp.id, left: r.left, right: r.right } : null; }).filter(Boolean);
        drag.current = { id: appt.id, appt, mode, startY: e.clientY, origStart: minsFromDate(appt.start), origEnd: minsFromDate(appt.end), origEmpId: appt.employee_id, day, cols };
        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", onPointerUp);
    };

    const createAt = (employeeId, day, yPx) => {
        const min = Math.max(DAY_START_MIN, Math.round((yPx / PX + DAY_START_MIN) / SNAP) * SNAP);
        const base = new Date(day); base.setHours(0, 0, 0, 0);
        const start = new Date(base.getTime() + min * 60000);
        setModal({ mode: "create", data: { employee_id: employeeId, customer_id: "", start, end: new Date(start.getTime() + 30 * 60000), notes: "", status: "scheduled" } });
    };

    const hours = []; for (let h = START_HOUR; h <= END_HOUR; h++) hours.push(h);
    const apptsByEmpDay = (empId, day) => { const dk = isoLocalDate(day); return (appts || []).filter((a) => (preview?.id === a.id ? preview.empId : a.employee_id) === empId && isoLocalDate(new Date(a.start)) === dk && a.status !== "cancelled"); };

    const label = view === "month" ? cursor.toLocaleDateString(lang, { month: "long", year: "numeric" })
        : view === "week" ? `${startOfWeek(cursor).toLocaleDateString(lang, { day: "numeric", month: "short" })} – ${addDays(startOfWeek(cursor), 6).toLocaleDateString(lang, { day: "numeric", month: "short" })}`
            : cursor.toLocaleDateString(lang, { weekday: "long", day: "numeric", month: "long" });

    return (
        <div>
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="icon" onClick={navPrev} className="rounded-full" data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
                    <Button variant="outline" size="icon" onClick={navNext} className="rounded-full" data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
                    <Button variant="ghost" onClick={() => setCursor(new Date())} className="rounded-full" data-testid="cal-today">{t("common.today")}</Button>
                    <h2 className="ml-2 font-serif text-xl font-bold capitalize">{label}</h2>
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex gap-1 rounded-full border border-border/70 bg-card p-1">
                        {[["day", t("common.day")], ["week", t("common.week")], ["month", t("common.month")]].map(([k, l]) => (
                            <button key={k} onClick={() => setView(k)} data-testid={`view-${k}`}
                                className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors duration-200 ${view === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
                        ))}
                    </div>
                    <Button variant="outline" onClick={() => setWlForm(true)} className="rounded-full" data-testid="cal-waitlist-btn">{t("wl.addShort")}</Button>
                    <Button onClick={() => activeEmps.length ? createAt(activeEmps[0].id, cursor, 9 * 60 - DAY_START_MIN) : toast.error(t("calendar.noEmployees"))} className="rounded-full" data-testid="new-appt-btn"><Plus className="mr-1 h-4 w-4" />{t("calendar.newAppointment")}</Button>
                </div>
            </div>

            {isLoading || !employees ? <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-accent" /></div>
                : activeEmps.length === 0 ? <EmptyState icon={CalendarDays} title={t("calendar.noEmployees")} />
                    : view === "month" ? <MonthView cursor={cursor} appts={appts || []} employees={activeEmps} lang={lang} onDay={(d) => { setCursor(d); setView("day"); }} />
                        : view === "week" ? <WeekView cursor={cursor} appts={appts || []} employees={activeEmps} hours={hours} lang={lang} t={t} onEdit={(a) => setModal({ mode: "edit", data: a })} />
                            : <DayView day={cursor} employees={activeEmps} apptsByEmpDay={apptsByEmpDay} hours={hours} beginDrag={beginDrag} preview={preview} createAt={createAt} onEdit={(a) => setModal({ mode: "edit", data: a })} t={t} gaps={gaps || []} refreshGaps={refreshGaps} />}

            {wlForm && <WaitlistForm onClose={() => setWlForm(false)} onSaved={() => { setWlForm(false); refreshGaps(); toast.success(t("common.saved")); }} t={t} />}
            {modal && <AppointmentModal modal={modal} setModal={setModal} employees={activeEmps} services={services || []} customers={customers || []} onSaved={invalidate} t={t} />}
            {dialog?.type === "warnings" && <WarningsDialog warnings={dialog.warnings} t={t} confirmLabel={t("sched.moveAnyway")} onCancel={() => { setDialog(null); invalidate(); }} onConfirm={dialog.onConfirm} />}
            {dialog?.type === "choice" && <ChoiceDialog title={dialog.title} options={dialog.options} t={t} onCancel={() => setDialog(null)} />}
        </div>
    );
}

function TimeGutter({ hours }) {
    return (
        <div className="w-14 shrink-0">
            <div className="h-10" />
            {hours.map((h) => <div key={h} style={{ height: 60 * PX }} className="relative"><span className="absolute -top-2 right-2 text-xs text-muted-foreground">{String(h).padStart(2, "0")}:00</span></div>)}
        </div>
    );
}

const hasPhases = (a) => (a.services || []).flatMap((s) => s.phases || []).length > 1 || (a.services || []).length > 1;

function PhaseStrips({ a, startMin, totalMin, t }) {
    const phases = (a.services || []).flatMap((s) => s.phases || []);
    if (!hasPhases(a)) return null;
    const segs = phases.length ? phases : (a.services || []).map((s) => ({ ...s, employee_required: true }));
    return (
        <div className="absolute inset-x-0 bottom-0 top-[18px] pointer-events-none">
            {segs.map((p, i) => {
                const ps = minsFromDate(p.start), pe = minsFromDate(p.end);
                const top = ((ps - startMin) / totalMin) * 100, h = ((pe - ps) / totalMin) * 100;
                const free = p.employee_required === false;
                return (
                    <div key={i} className="absolute inset-x-0 border-t border-white/30 px-2 text-[9px] leading-tight" style={{ top: `${top}%`, height: `${h}%`, background: free ? "repeating-linear-gradient(135deg, rgba(255,255,255,0.25) 0 4px, transparent 4px 8px)" : "transparent" }}
                        data-testid={`phase-strip-${i}`}>
                        {h > 8 && <span className={free ? "italic opacity-95" : "opacity-85"}>{fmt(p.start)} {p.name}{free ? ` · ${t("sched.processingFree")}` : ""}</span>}
                    </div>
                );
            })}
        </div>
    );
}

function ApptBlock({ a, top, height, onEdit, beginDrag, day, draggable, previewState, t }) {
    const total = Math.max(height / PX, 1);
    const startMin = previewState ? previewState.start : minsFromDate(a.start);
    const res = previewState?.result;
    const badge = previewState ? (!res ? null : res.conflicts?.length ? { cls: "bg-red-600", icon: X, txt: t("sched.unavailable") } : res.warnings?.length ? { cls: "bg-amber-500", icon: AlertTriangle, txt: t("sched.checkRequired") } : { cls: "bg-emerald-600", icon: Check, txt: t("sched.available") }) : null;
    return (
        <div data-testid={`appt-${a.id}`}
            onPointerDown={draggable ? (e) => beginDrag(e, a, "move", day) : undefined}
            onClick={(e) => { e.stopPropagation(); if (!previewState) onEdit(a); }}
            className={`absolute left-1 right-1 overflow-hidden rounded-lg px-2 py-1 text-white shadow-sm transition-shadow duration-150 hover:shadow-md ${previewState ? "ring-2 ring-white/70 shadow-xl z-10" : ""}`}
            style={{ top, height: Math.max(height, 18), background: a.employee_color, cursor: draggable ? "grab" : "pointer", opacity: a.status === "completed" ? 0.85 : previewState ? 0.92 : 1 }}>
            <div className="flex items-center justify-between">
                <p className="truncate text-[11px] font-bold leading-tight">{a.customer_name}</p>
                <span className="ml-1 shrink-0 rounded px-1 text-[9px] font-semibold" style={{ background: "rgba(255,255,255,0.25)" }}>{previewState ? `${fmtMin(previewState.start)}–${fmtMin(previewState.end)}` : fmt(a.start)}</span>
            </div>
            {height > 30 && !previewState && !(height > 60 && hasPhases(a)) && <p className="truncate text-[10px] leading-tight opacity-90">{a.service_name || ""}{a.price ? ` · €${Number(a.price).toFixed(0)}` : ""}</p>}
            {!previewState && height > 60 && <PhaseStrips a={a} startMin={startMin} totalMin={total} t={t} />}
            {badge && (
                <div className={`absolute left-1 top-5 flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${badge.cls}`} data-testid="drag-badge">
                    <badge.icon className="h-3 w-3" />{badge.txt}
                </div>
            )}
            {draggable && <div onPointerDown={(e) => beginDrag(e, a, "resize", day)} className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize" style={{ background: "rgba(0,0,0,0.12)" }} data-testid={`resize-${a.id}`} />}
        </div>
    );
}

function DayView({ day, employees, apptsByEmpDay, hours, beginDrag, preview, createAt, onEdit, t, gaps, refreshGaps }) {
    return (
        <div className="overflow-x-auto rounded-2xl border border-border/70 bg-card">
            <div className="flex min-w-[600px]">
                <TimeGutter hours={hours} />
                <div className="flex flex-1">
                    {employees.map((emp) => (
                        <div key={emp.id} className="flex-1 border-l border-border/60">
                            <div className="flex h-10 items-center gap-2 border-b border-border/60 px-2">
                                <span className="h-3 w-3 rounded-full" style={{ background: emp.color }} />
                                <span className="truncate text-sm font-semibold">{emp.name}</span>
                            </div>
                            <div className="relative" style={{ height: GRID_HEIGHT }} onClick={(e) => { const rect = e.currentTarget.getBoundingClientRect(); createAt(emp.id, day, e.clientY - rect.top); }} data-testid={`day-col-${emp.id}`}>
                                {hours.map((h, i) => <div key={h} className="absolute left-0 right-0 border-b border-border/40" style={{ top: i * 60 * PX }} />)}
                                {gaps.filter((g) => g.employee_id === emp.id).map((g) => {
                                    const s = minsFromDate(g.start), e = minsFromDate(g.end);
                                    return <GapBlock key={g.start} gap={g} top={(s - DAY_START_MIN) * PX + 2} height={(e - s) * PX - 4} t={t} onChanged={refreshGaps} />;
                                })}
                                {apptsByEmpDay(emp.id, day).map((a) => {
                                    const pv = preview && preview.id === a.id ? preview : null;
                                    const startMin = pv ? pv.start : minsFromDate(a.start);
                                    const endMin = pv ? pv.end : minsFromDate(a.end);
                                    if (a.status === "reserved") return <HoldBlock key={a.id} a={a} top={(startMin - DAY_START_MIN) * PX} height={(endMin - startMin) * PX} t={t} />;
                                    return <ApptBlock key={a.id} a={a} day={day} top={(startMin - DAY_START_MIN) * PX} height={(endMin - startMin) * PX} onEdit={onEdit} beginDrag={beginDrag} draggable previewState={pv} t={t} />;
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <p className="border-t border-border/60 px-4 py-2 text-xs text-muted-foreground">{t("calendar.dragHint")}</p>
        </div>
    );
}

function WeekView({ cursor, appts, employees, hours, lang, t, onEdit }) {
    const ws = startOfWeek(cursor);
    const days = Array.from({ length: 7 }, (_, i) => addDays(ws, i));
    const empColor = (id) => employees.find((e) => e.id === id)?.color || "#9ca3af";
    return (
        <div className="overflow-x-auto rounded-2xl border border-border/70 bg-card">
            <div className="flex min-w-[800px]">
                <TimeGutter hours={hours} />
                <div className="flex flex-1">
                    {days.map((d) => (
                        <div key={d.toISOString()} className="flex-1 border-l border-border/60">
                            <div className="flex h-10 flex-col items-center justify-center border-b border-border/60">
                                <span className="text-xs text-muted-foreground">{d.toLocaleDateString(lang, { weekday: "short" })}</span>
                                <span className="text-sm font-semibold">{d.getDate()}</span>
                            </div>
                            <div className="relative" style={{ height: GRID_HEIGHT }}>
                                {hours.map((h, i) => <div key={h} className="absolute left-0 right-0 border-b border-border/40" style={{ top: i * 60 * PX }} />)}
                                {appts.filter((a) => isoLocalDate(new Date(a.start)) === isoLocalDate(d) && a.status !== "cancelled").map((a) => {
                                    const s = minsFromDate(a.start), e = minsFromDate(a.end);
                                    return <ApptBlock key={a.id} a={{ ...a, employee_color: empColor(a.employee_id) }} top={(s - DAY_START_MIN) * PX} height={(e - s) * PX} onEdit={onEdit} draggable={false} t={t} />;
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function MonthView({ cursor, appts, employees, lang, onDay }) {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const startPad = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startPad; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    const empColor = (id) => employees.find((e) => e.id === id)?.color || "#9ca3af";
    const dayNames = Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(new Date()), i).toLocaleDateString(lang, { weekday: "short" }));
    return (
        <div className="rounded-2xl border border-border/70 bg-card p-3">
            <div className="mb-2 grid grid-cols-7 gap-1 text-center text-xs font-medium text-muted-foreground">{dayNames.map((n) => <div key={n}>{n}</div>)}</div>
            <div className="grid grid-cols-7 gap-1">
                {cells.map((d, i) => {
                    if (!d) return <div key={i} className="min-h-[92px] rounded-lg bg-secondary/20" />;
                    const dayAppts = appts.filter((a) => isoLocalDate(new Date(a.start)) === isoLocalDate(d) && a.status !== "cancelled");
                    const isToday = isoLocalDate(d) === isoLocalDate(new Date());
                    return (
                        <button key={i} onClick={() => onDay(d)} data-testid={`month-day-${d.getDate()}`} className="min-h-[92px] rounded-lg border border-border/50 p-1.5 text-left transition-colors duration-200 hover:border-accent hover:bg-accent/5">
                            <span className={`text-xs font-semibold ${isToday ? "flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground" : ""}`}>{d.getDate()}</span>
                            <div className="mt-1 space-y-0.5">
                                {dayAppts.slice(0, 3).map((a) => <div key={a.id} className="truncate rounded px-1 text-[10px] text-white" style={{ background: empColor(a.employee_id) }}>{fmt(a.start)} {a.customer_name}</div>)}
                                {dayAppts.length > 3 && <p className="text-[10px] text-muted-foreground">+{dayAppts.length - 3}</p>}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
